<template>
  <div class="split-account-container">
    <!-- 收款方列表 -->
    <div
      v-for="(recipient, index) in recipients"
      :key="index"
      class="recipient-section"
    >
      <div class="recipient-header">
        收款方{{ index + 1 }}
      </div>

      <!-- 分账比例 -->
      <div class="form-group">
        <label class="form-label">分账比例</label>
        <div class="input-wrapper">
          <input
            v-model.number="recipient.splitRatio"
            type="number"
            class="ratio-input"
            :min="1"
            :max="30"
            placeholder="请输入分账比例"
            @input="handleRatioInput(index, $event)"
          />
          <span class="suffix">%</span>
        </div>
        <div class="hint-text">最高30%，最低1%</div>
      </div>

      <!-- 收款类型 -->
      <div class="form-group">
        <label class="form-label">
          收款类型
        </label>
        <div class="radio-group">
          <label class="radio-label">
            <input
              v-model="recipient.splitType"
              type="radio"
              value="MERCHANT"
              class="radio-input"
              @change="handleTypeChange(index, 'MERCHANT')"
            />
            <span class="radio-custom"></span>
            <span class="radio-text">手机号直分</span>
          </label>
          <label class="radio-label">
            <input
              v-model="recipient.splitType"
              type="radio"
              value="ONLY_NUM"
              class="radio-input"
              @change="handleTypeChange(index, 'ONLY_NUM')"
            />
            <span class="radio-custom"></span>
            <span class="radio-text">仅作记录</span>
          </label>
        </div>
      </div>

      <!-- 手机号/OpenID搜索（仅在手机号直分时显示） -->
      <div v-if="recipient.splitType === 'MERCHANT'" class="form-group">
        <label class="form-label">
          搜索用户
          <span class="required">*</span>
        </label>
        <div class="search-wrapper">
          <div class="search-input-wrapper" :class="{ 'has-selection': recipient.receiverAccount }">
            <input
              v-model="recipient.receiverAccount"
              type="text"
              class="search-input"
              @blur="updateParentModel"
              placeholder="请输入手机号或用户openid搜索"
            />
<!--            <button-->
<!--              class="search-btn"-->
<!--              @click="handleSearchClick(index)"-->
<!--              :disabled="!recipient.searchKeyword || recipient.searchKeyword.length < 2"-->
<!--            >-->
<!--              <svg class="search-icon" viewBox="0 0 20 20" fill="currentColor">-->
<!--                <path fill-rule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clip-rule="evenodd" />-->
<!--              </svg>-->
<!--            </button>-->
          </div>
          <!-- 搜索结果列表 -->
<!--          <div v-if="showSearchResults[index]" class="search-results">-->
<!--            <div v-if="searchResults[index] && searchResults[index].length > 0">-->
<!--              <div-->
<!--                v-for="(user, userIndex) in searchResults[index]"-->
<!--                :key="userIndex"-->
<!--                class="search-result-item"-->
<!--                @click="selectUser(index, user)"-->
<!--              >-->
<!--                <div class="user-info">-->
<!--                  <span class="user-name">{{ user.name }}</span>-->
<!--                  <span class="user-phone">{{ user.phone }}</span>-->
<!--                </div>-->
<!--                <div class="user-openid">{{ user.openId }}</div>-->
<!--              </div>-->
<!--            </div>-->
<!--            <div v-else-if="recipient.searchKeyword && recipient.searchKeyword.length >= 2" class="no-results">-->
<!--              <div v-if="isSearching[index]">搜索中...</div>-->
<!--              <div v-else>未找到匹配的用户</div>-->
<!--            </div>-->
<!--          </div>-->
        </div>
        <div class="hint-text">
          如未搜索到用户，先让用户登录兔魅力小程序后再试
        </div>
      </div>


      <!-- 收款方姓名/名称 -->
      <div class="form-group">
        <label class="form-label">
          {{ recipient.splitType === 'MERCHANT' ? '收款方姓名' : '收款方名称' }}
          <span class="required">*</span>
        </label>
        <input
          v-model="recipient.receiverName"
          type="text"
          class="name-input"
          :placeholder="recipient.splitType === 'MERCHANT' ? '请输入收款方姓名' : '请输入收款方名称'"
        />
      </div>


      <!-- 提示文字（仅在仅作记录时显示） -->
      <div v-if="recipient.splitType === 'ONLY_NUM'" class="hint-text form-hint">
        该选项不做分账，仅作订单分账明细记录，便于业务线下打款
      </div>
    </div>

    <!-- 增加收款方按钮 -->
    <button
      class="add-recipient-btn"
      @click="addRecipient"
      :disabled="recipients.length >= 5"
    >
      + 增加收款方
    </button>
  </div>
</template>

<script setup lang="ts">
import {ref, computed, watch, reactive, nextTick} from 'vue'

// 用户类型定义
interface User {
  id: string
  name: string
  phone: string
  openId: string
}

// 收款方类型定义
interface Recipient {
  receiverOrder:number,
  splitRatio: number | null
  splitType: 'MERCHANT' | 'ONLY_NUM' | ''
  receiverName: string
  searchKeyword?: string
  receiverAccount?: User
}

const props = withDefaults(
  defineProps<{
    model: Record<string, any>
    prop: string
    hidden?: boolean
    disabled?: boolean,
    labelProp: string
  }>(),
  {
    hidden: false,
    disabled: false,
  }
)

// 收款方列表
const recipients = ref<Recipient[]>([
  {
    receiverOrder:1,
    splitRatio: null,
    splitType: '',
    receiverName: ''
  }
])

// 搜索相关状态
const showSearchResults = reactive<boolean[]>([])
const searchResults = reactive<User[][]>([])
const isSearching = reactive<boolean[]>([])

// 模拟用户数据
const mockUsers: User[] = [
  { id: '1', name: '张三', phone: '13800138000', openId: 'openid_001' },
  { id: '2', name: '李四', phone: '13800138001', openId: 'openid_002' },
  { id: '3', name: '王五', phone: '13800138002', openId: 'openid_003' },
  { id: '4', name: '赵六', phone: '13800138003', openId: 'openid_004' },
]

// 总比例计算
const totalRatio = computed(() => {
  return recipients.value.reduce((sum, recipient) => {
    return sum + (recipient.splitRatio || 0)
  }, 0)
})

// 处理分账比例输入
const handleRatioInput = (index: number, event: Event) => {
  const input = event.target as HTMLInputElement
  let value = parseInt(input.value) || 0

  if (value < 1) {
    value = 1
  } else if (value > 30) {
    value = 30
  }

  recipients.value[index].splitRatio = value

  if (totalRatio.value > 100) {
    const exceed = totalRatio.value - 100
    recipients.value[index].splitRatio = Math.max(1, (recipients.value[index].splitRatio || 0) - exceed)
  }

  updateParentModel()
}

// 处理收款类型变化
const handleTypeChange = (index: number, type: 'MERCHANT' | 'ONLY_NUM') => {
  recipients.value[index].splitType = type
  // if (type === 'ONLY_NUM') {
  //   showSearchResults[index] = false
  //   searchResults[index] = []
  // }
  updateParentModel()
}

// 处理搜索输入
const handleSearchInput = (index: number, event: Event) => {
  const input = event.target as HTMLInputElement
  const keyword = input.value.trim()
  recipients.value[index].searchKeyword = keyword

  if (keyword.length >= 2) {
    showSearchResults[index] = true
    performSearch(index, keyword)
  } else {
    showSearchResults[index] = false
  }
}

// 处理搜索按钮点击
const handleSearchClick = (index: number) => {
  const keyword = recipients.value[index].searchKeyword
  if (keyword && keyword.length >= 2) {
    showSearchResults[index] = true
    performSearch(index, keyword.trim())
  }
}

// 执行搜索
const performSearch = (index: number, keyword: string) => {
  isSearching[index] = true

  // 模拟API调用延迟
  setTimeout(() => {
    const results = mockUsers.filter(user =>
      user.phone.includes(keyword) ||
      user.openId.includes(keyword) ||
      user.name.includes(keyword)
    )
    searchResults[index] = results
    isSearching[index] = false
  }, 500)
}

// 选择用户
const selectUser = (index: number, user: User) => {
  recipients.value[index].receiverAccount = user
  recipients.value[index].receiverName = user.name
  showSearchResults[index] = false
  props.model[props.labelProp] = recipients.value
}

// 点击外部关闭搜索结果
const closeSearchResults = () => {
  recipients.value.forEach((_, index) => {
    showSearchResults[index] = false
  })
}

// 添加收款方
const addRecipient = () => {
  if (recipients.value.length >= 11) {
    alert('最多只能添加10个收款方')
    return
  }

  const lastIndex = recipients.value.length - 1
  if (lastIndex < 0) {
    addNewRecipient()
    return
  }

  const lastRecipient = recipients.value[lastIndex]
  const validationError = validateRecipient(lastRecipient, lastIndex + 1)

  if (validationError) {
    alert(validationError)
    return
  }

  if (totalRatio.value > 30) {
    alert(`当前总分账比例为${totalRatio.value}%，已超过30%。请调整后再添加。`)
    return
  }

  addNewRecipient()
}

// 验证单个收款方
const validateRecipient = (recipient: Recipient, index: number): string | null => {
  if (!recipient.splitRatio || recipient.splitRatio < 1 || recipient.splitRatio > 30) {
    return `收款方${index}的分账比例需在1%-30%之间`
  }

  if (!recipient.splitType) {
    return `请选择收款方${index}的收款类型`
  }

  if (!recipient.receiverName?.trim()) {
    return `收款方${index}的${recipient.splitType === 'MERCHANT' ? '姓名' : '名称'}不能为空`
  }

  if (recipient.splitType === 'MERCHANT' && !recipient.receiverAccount) {
    return `收款方${index}需要输入手机号或用户openid`
  }

  return null
}

// 添加新收款方的辅助函数
const addNewRecipient = () => {

  recipients.value.push({
    receiverOrder:recipients.value.length + 1,
    splitRatio: null,
    splitType: '',
    receiverName: ''
  })
  showSearchResults.push(false)
  searchResults.push([])
  isSearching.push(false)
  updateParentModel()

}

// 更新父组件模型的方法
const updateParentModel = () => {
  // 使用 nextTick 确保 DOM 更新完成后再更新父组件
  nextTick(() => {
    props.model[props.labelProp] = recipients.value
  })
}

</script>

<style scoped lang="scss">
.split-account-container {
  width: 100%;
  box-sizing: border-box;
  max-width: 600px;
  margin: 0 auto;
  padding: 24px;
  background: #fff;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
}

.recipient-section {
  width: 100%;
  box-sizing: border-box;
  border: 1px solid #e8e8e8;
  border-radius: 8px;
  padding: 20px;
  margin-bottom: 20px;
  background: #fafafa;
  position: relative;

  &:last-of-type {
    margin-bottom: 0;
  }
}

.recipient-header {
  font-size: 16px;
  font-weight: 600;
  color: #333;
  margin-bottom: 20px;
  padding-bottom: 12px;
  border-bottom: 1px solid #f0f0f0;
}

.form-group {
  margin-bottom: 16px;

  &:last-child {
    margin-bottom: 0;
  }
}

.form-label {
  display: block;
  font-size: 14px;
  color: #333;
  margin-bottom: 8px;
  font-weight: 500;
  .required {
    color: #ff4d4f;
    margin-left: 4px;
  }
}

.input-wrapper {
  display: flex;
  align-items: center;
  border: 1px solid #d9d9d9;
  border-radius: 4px;
  background: #fff;
  transition: all 0.3s;
  overflow: hidden;
  width: 200px;

  &:focus-within {
    border-color: #1890ff;
    box-shadow: 0 0 0 2px rgba(24, 144, 255, 0.2);
  }
}

.ratio-input {
  flex: 1;
  height: 36px;
  padding: 0 12px;
  border: none;
  outline: none;
  font-size: 14px;
  background: transparent;

  &::placeholder {
    color: #bfbfbf;
  }

  &::-webkit-inner-spin-button,
  &::-webkit-outer-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  -moz-appearance: textfield;
}

.suffix {
  padding: 0 12px;
  font-size: 14px;
  color: #666;
  background: #fafafa;
  height: 36px;
  display: flex;
  align-items: center;
  border-left: 1px solid #d9d9d9;
}

// 搜索框样式
.search-wrapper {
  position: relative;
  width: 300px;
}

.search-input-wrapper {
  display: flex;
  align-items: center;
  border: 1px solid #d9d9d9;
  border-radius: 4px;
  background: #fff;
  transition: all 0.3s;
  overflow: hidden;

  &.has-selection {
    background: #f0f8ff;
    border-color: #91d5ff;
  }

  &:focus-within {
    border-color: #1890ff;
    box-shadow: 0 0 0 2px rgba(24, 144, 255, 0.2);
  }
}

.search-input {
  flex: 1;
  height: 36px;
  padding: 0 12px;
  border: none;
  outline: none;
  font-size: 14px;
  background: transparent;

  &::placeholder {
    color: #bfbfbf;
  }
}

.search-btn {
  width: 40px;
  height: 36px;
  border: none;
  background: transparent;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #666;
  transition: all 0.3s;

  &:hover:not(:disabled) {
    color: #1890ff;
  }

  &:disabled {
    cursor: not-allowed;
    color: #bfbfbf;
  }
}

.search-icon {
  width: 16px;
  height: 16px;
}

// 搜索结果列表
.search-results {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  margin-top: 4px;
  border: 1px solid #e8e8e8;
  border-radius: 4px;
  background: #fff;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  z-index: 1000;
  max-height: 300px;
  overflow-y: auto;
}

.search-result-item {
  padding: 12px 16px;
  cursor: pointer;
  border-bottom: 1px solid #f0f0f0;
  transition: all 0.3s;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background: #f5f5f5;
  }

  .user-info {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 4px;

    .user-name {
      font-weight: 500;
      color: #333;
    }

    .user-phone {
      color: #666;
      font-size: 12px;
    }
  }

  .user-openid {
    font-size: 12px;
    color: #999;
    word-break: break-all;
  }
}

.no-results {
  padding: 20px 16px;
  text-align: center;
  color: #999;
  font-size: 14px;
}

.name-input {
  width: 300px;
  height: 36px;
  padding: 0 12px;
  border: 1px solid #d9d9d9;
  border-radius: 4px;
  font-size: 14px;
  transition: all 0.3s;
  background: #fff;

  &:focus {
    outline: none;
    border-color: #1890ff;
    box-shadow: 0 0 0 2px rgba(24, 144, 255, 0.2);
  }

  &::placeholder {
    color: #bfbfbf;
  }
}

.hint-text {
  font-size: 12px;
  color: #999;
  margin-top: 6px;
  line-height: 1.5;
}

.form-hint {
  margin-top: 8px;
  padding: 8px 12px;
  background: #f0f0f0;
  border-radius: 4px;
  border-left: 3px solid #999;
}

.radio-group {
  display: flex;
  gap: 24px;
}

.radio-label {
  display: flex;
  align-items: center;
  cursor: pointer;
  user-select: none;
}

.radio-input {
  display: none;

  &:checked + .radio-custom {
    border-color: #1890ff;

    &::after {
      transform: scale(1);
    }
  }
}

.radio-custom {
  width: 16px;
  height: 16px;
  border: 1px solid #d9d9d9;
  border-radius: 50%;
  margin-right: 8px;
  position: relative;
  transition: all 0.3s;

  &::after {
    content: '';
    position: absolute;
    top: 3px;
    left: 3px;
    width: 8px;
    height: 8px;
    background: #1890ff;
    border-radius: 50%;
    transform: scale(0);
    transition: transform 0.3s;
  }
}

.radio-text {
  font-size: 14px;
  color: #333;
}

.add-recipient-btn {
  width: 100%;
  height: 40px;
  background: #fff;
  border: 1px dashed #d9d9d9;
  border-radius: 4px;
  color: #1890ff;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.3s;
  margin-top: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;

  &:hover:not(:disabled) {
    border-color: #1890ff;
    background: #f0f8ff;
  }

  &:disabled {
    color: #bfbfbf;
    cursor: not-allowed;
    border-color: #f0f0f0;
  }
}
</style>
